import React from 'react';
import Select, { StylesConfig } from 'react-select';

export type SelectOption = {
    value: string;
    label: string;
    /** Shown in the closed control instead of `label` — for narrow fields whose
     *  full label only exists to make the option searchable (e.g. "USD — US Dollar"). */
    shortLabel?: string;
    isDisabled?: boolean;
};

type Rounded = 'all' | 'left' | 'right' | 'none';

interface SearchableSelectProps {
    options: SelectOption[];
    /** Current value. Compared with String() so numeric ids work. */
    value?: string | number | null;
    /**
     * Called with a synthetic event shaped like a native <select> change,
     * so existing handlers (`handleShipmentChange`, `getStateTo`, …) work unchanged.
     */
    onChange: (event: { target: { name: string; value: string } }) => void;
    name?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    isClearable?: boolean;
    /** Matches the rounded-l / rounded-r halves used by the amount+currency input groups. */
    rounded?: Rounded;
    className?: string;
    id?: string;
}

const RADIUS: Record<Rounded, string> = {
    all: '5px',
    left: '5px 0 0 5px',
    right: '0 5px 5px 0',
    none: '0',
};

/**
 * Drop-in replacement for the plain `<select>` used across the admin forms —
 * same look, plus type-to-search. Styling mirrors
 * `bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] p-2`.
 */
function SearchableSelect({
    options,
    value,
    onChange,
    name = '',
    placeholder = 'Select One',
    disabled = false,
    required = false,
    isClearable = false,
    rounded = 'all',
    className = '',
    id,
}: SearchableSelectProps) {
    const selected =
        value === undefined || value === null || value === ''
            ? null
            : options.find((option) => String(option.value) === String(value)) ?? null;

    const styles: StylesConfig<SelectOption, false> = {
        control: (base, state) => ({
            ...base,
            minHeight: '38px',
            backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
            borderColor: state.isFocused ? '#E04343' : '#DADADA',
            borderWidth: '0.5px',
            borderRadius: RADIUS[rounded],
            boxShadow: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            ':hover': { borderColor: state.isFocused ? '#E04343' : '#DADADA' },
        }),
        valueContainer: (base) => ({ ...base, padding: '0 8px' }),
        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '14px', color: '#333333' }),
        singleValue: (base) => ({ ...base, fontSize: '14px', color: '#333333' }),
        placeholder: (base) => ({ ...base, fontSize: '12px', color: '#DADADA' }),
        indicatorsContainer: (base) => ({ ...base, height: '38px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, padding: '0 6px', color: '#808080' }),
        clearIndicator: (base) => ({ ...base, padding: '0 2px', color: '#808080' }),
        menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
        // Portalled so the menu is never clipped by a modal or an overflow container.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            fontSize: '13px',
            backgroundColor: state.isSelected ? '#E04343' : state.isFocused ? '#FFF5F5' : '#FFFFFF',
            color: state.isSelected ? '#FFFFFF' : '#333333',
            cursor: 'pointer',
        }),
    };

    return (
        <Select
            inputId={id}
            className={className}
            classNamePrefix="searchable-select"
            options={options}
            value={selected}
            onChange={(option) =>
                onChange({ target: { name, value: option ? String((option as SelectOption).value) : '' } })
            }
            placeholder={placeholder}
            isDisabled={disabled}
            isClearable={isClearable}
            isSearchable
            required={required}
            name={name}
            styles={styles}
            formatOptionLabel={(option, meta) =>
                meta.context === 'value' && option.shortLabel ? option.shortLabel : option.label
            }
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            noOptionsMessage={() => 'No results'}
        />
    );
}

export default SearchableSelect;
